import {
  RightOutlined,
  PlusSquareOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Cascader, Divider, Space, Spin, Tag } from 'antd';
import React, { Fragment, ReactNode, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { isInternal } from '@/infrastructure/auth';
import { getAppIcon, getPlatformName } from '@/infrastructure/utils/app';
import { getCachedFilters } from '@/presentation/components/form/utils';
import { CreateApp } from '@/presentation/components/my-app-list/create-app';
import { PRODUCTS_SORT_STORAGE } from '@/presentation/config/storage';
import { useStores } from '@/presentation/store';
import { ProductInfo } from '@/service/user';
import style from './index.module.scss';

interface SelectedOptionsType {
  key: string;
  value: string;
  label: ReactNode;
  pid: string;
  appName: string;
}

export const AppList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const appRef = useRef<HTMLDivElement>(null);
  const { setId, productId, product, products, loadProducts } = useStores().ProductStore;
  const [loadStatus, setLoadStatus] = useState<undefined | 'loading' | 'loaded'>();
  const [visible, setVisible] = useState(false);
  const storageProducts = getCachedFilters(PRODUCTS_SORT_STORAGE) || [];
  const onCascaderClick = async () => {
    if (!loadStatus) {
      setLoadStatus('loading');
      await loadProducts();
      setLoadStatus('loaded');
    }
  };


  const labelRender = (app?: ProductInfo) => (app ? <div className={style.appNameItem}>
    {getAppIcon(`${app?.platformId}`)}
    <span>{app?.productName ?? ''}</span>
  </div> : null);
  const options: SelectedOptionsType[] = useMemo(() => {
    const appList = products.map((app: ProductInfo) => ({
      key: app.productId,
      value: app.productId,
      appName: app.productName,
      pid: app.platformId,
      label: labelRender(app),
    }));
    if (!Array.isArray(storageProducts)) {
      return appList;
    }
    return appList.sort((a: any, b: any) => {
      const aIndex = storageProducts.findIndex(str => str === a.value);
      const bIndex = storageProducts.findIndex(str => str === b.value);
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return bIndex;
      }
      return aIndex - bIndex;
    });
  }, [products, storageProducts, productId]);


  const onChange: any = (_: string[], selectedOptions: SelectedOptionsType[]) => {
    const appId = selectedOptions[0].value;
    setId(appId, true);
    history.push('/overview');
  };

  const filter = (inputValue: string, path: any[]) => (
    path.some(option => option.appName?.toLowerCase()?.indexOf(inputValue.toLowerCase()) > -1)
  );

  const dropdownRender = (menus: ReactNode) => {
    if (loadStatus !== 'loaded') {
      return <div className={style.spinBox}>
        <Spin/>
      </div>;
    }

    return (
      <div className={style.appList}>
        <div className={style.appListTitle}>{t('我参与的产品')}</div>
        {menus}
        <div className={style.appListItem} onClick={() => history.push('/workbench/apps')}>
          <Space>
            <AppstoreOutlined />
            <span>{t('全部产品')}</span>
          </Space>
          <RightOutlined className={style.suffixIcon}/>
        </div>
        {/* OA对外版本不可以创建产品 */}
        {!isInternal() && <Fragment>
          <Divider style={{ margin: '4px 0' }} />
          <div
            className={style.appListItem}
            onClick={() => setVisible(true)}
          >
            <Space>
              <PlusSquareOutlined/>
              <span>{t('创建产品')}</span>
            </Space>
            <RightOutlined className={style.suffixIcon}/>
          </div>
        </Fragment>}
      </div>
    );
  };

  return (
    <div ref={appRef} className={style.productSelector}>
      <Cascader
        options={options}
        value={[productId]}
        allowClear={false}
        showSearch={{ filter }}
        getPopupContainer={() => appRef?.current as HTMLDivElement}
        style={{ minWidth: '200px' }}
        onChange={onChange}
        displayRender={() => labelRender(product)}
        dropdownRender={dropdownRender}
        placeholder={t('请选择产品')}
        onClick={onCascaderClick}
      />
      {product && (
        <div className={style.platformName}>
          <Tag color='#EDF1F7' style={{ color: '#0A6CFF' }}>{getPlatformName(Number(product.platformId))}</Tag>
        </div>
      )}
      <CreateApp visible={visible} handleCancel={() => setVisible(false)} />
    </div>
  );
};
